import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';
import { PLATFORM_ID, inject } from '@angular/core';
import { HomeDashboardService } from '../../data/services/view/home-dashboard.service';
import { DataTableService } from '@data/services/utils/data-table.service';
import { LoaderService } from '@data/services/page/loader.service';
import { MessageService } from 'primeng/api';
import {
  PendingBalancesResponse,
  PendingBalancesByClient,
  DashboardMetrics,
  OverdueRangeOption,
  MonthlyDebtMatrix,
  MatrixMonth,
  MonthlyCellData,
  RowGrouping,
  GroupingOption,
  PendingBalanceByMonth
} from '@data/interfaces/pending-balances.interface';

interface SortableColumn {
  field: string;
  label: string;
  sortKey: string;
}

@Component({
  selector: 'app-pending-balances',
  templateUrl: './pending-balances.component.html',
  styleUrls: ['./pending-balances.component.scss']
})
export class PendingBalancesComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  platformId = inject(PLATFORM_ID);

  // Data properties
  clientsData: PendingBalancesByClient[] = [];        // raw del API (siempre por arrendamiento)
  displayClientsData: PendingBalancesByClient[] = []; // después de agrupar según groupBy
  filteredClientsData: PendingBalancesByClient[] = []; // displayClientsData tras filtros/sort
  monthlyTotals: { [month: string]: number } = {};
  metrics: DashboardMetrics = {
    totalClients: 0,
    totalDebt: 0,
    averageOverdueDays: 0,
    criticalClients: 0,
    monthWithHighestDebt: ''
  };

  // Chart data
  monthlyTotalsChart: any;
  overdueAnalysisChart: any;
  clientMonthlyChart: any;

  // Chart options
  barChartOptions: any;
  overdueRangeChartOptions: any;
  scatterChartOptions: any;
  lineChartOptions: any;

  // UI properties
  loading = false;
  selectedClient: PendingBalancesByClient | null = null;
  displayMonthlyModal = false;

  // Monthly Debt Matrix properties
  monthlyDebtMatrix: MonthlyDebtMatrix[] = [];
  matrixMonths: MatrixMonth[] = [];
  showMonthlyMatrix = true;

  // Agrupación: por arrendamiento (default) o por cliente
  groupBy: RowGrouping = 'lease';
  groupingOptions: GroupingOption[] = [
    { label: 'Por Arrendamiento', value: 'lease', icon: 'pi pi-file' },
    { label: 'Por Cliente', value: 'client', icon: 'pi pi-users' }
  ];

  // Filter properties
  debtRange: number[] = [0, 1000000];
  maxDebt = 1000000;
  selectedOverdueRange: OverdueRangeOption | null = null;
  globalFilter = '';

  // Sorting properties
  sortField: string | null = null;
  sortOrder: 'asc' | 'desc' | null = null;
  sortableColumns = ['totalAccumulatedDebt', 'totalOverdueDays', 'pendingBalancesByMonth.length'];

  sortableColumnConfig: SortableColumn[] = [
    { field: 'totalAccumulatedDebt', label: 'Deuda Total', sortKey: 'debt' },
    { field: 'totalOverdueDays', label: 'Días Atraso', sortKey: 'days' },
    { field: 'periods', label: 'Períodos', sortKey: 'periods' }
  ];

  overdueRanges: OverdueRangeOption[] = [
    { label: 'Todos', value: 'all', min: 0, max: 999 },
    { label: '1-30 días', value: '1-30', min: 1, max: 30 },
    { label: '31-60 días', value: '31-60', min: 31, max: 60 },
    { label: '61-90 días', value: '61-90', min: 61, max: 90 },
    { label: 'Más de 90 días', value: '90+', min: 91, max: 999 }
  ];

  constructor(
    private readonly dashboardService: HomeDashboardService,
    private cd: ChangeDetectorRef,
    private dataTableService: DataTableService,
    private loaderService: LoaderService,
    private messageService: MessageService,
    private router: Router
  ) {
    this.initializeChartOptions();
  }

  ngOnInit(): void {
    this.loadPendingBalances();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadPendingBalances(): void {
    this.loading = true;
    this.dashboardService.pendingBalances()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: { data: PendingBalancesResponse }) => {
          const data = response.data;

          if (data && data.clientsData && Array.isArray(data.clientsData)) {
            this.clientsData = data.clientsData.map(c => ({
              ...c,
              rowType: 'lease' as RowGrouping,
              leaseCount: 1,
              leaseIds: c.id ? [c.id] : [],
              leaseFolios: [c.folio]
            }));
            this.monthlyTotals = data.monthlyTotals || {};
            this.rebuildDisplayData();
            this.calculateMetrics(data);
            this.generateCharts(data);
            this.applySortingAndFilters();
          } else {
            console.warn('No se encontraron datos válidos de saldos pendientes');
            this.clientsData = [];
            this.displayClientsData = [];
            this.filteredClientsData = [];
            this.monthlyTotals = {};
            this.calculateMetrics({ clientsData: [], monthlyTotals: {} });
          }

          this.loading = false;
          this.cd.markForCheck();
        },
        error: (error) => {
          console.error('Error loading pending balances:', error);
          this.clientsData = [];
          this.displayClientsData = [];
          this.filteredClientsData = [];
          this.monthlyTotals = {};
          this.loading = false;
          this.cd.markForCheck();
        }
      });
  }

  // Reconstruye displayClientsData a partir de clientsData según groupBy
  private rebuildDisplayData(): void {
    if (this.groupBy === 'lease') {
      this.displayClientsData = [...this.clientsData];
    } else {
      this.displayClientsData = this.groupByClient(this.clientsData);
    }
    this.generateMonthlyDebtMatrix(this.displayClientsData);
  }

  // Consolida por cliente: suma expected/paid/pending por mes sobre todos sus arrendamientos.
  private groupByClient(rows: PendingBalancesByClient[]): PendingBalancesByClient[] {
    const byClient = new Map<string, PendingBalancesByClient>();

    rows.forEach(row => {
      const key = row.clientId || row.clientName;
      const existing = byClient.get(key);

      if (!existing) {
        byClient.set(key, {
          ...row,
          folio: row.folio,
          rowType: 'client',
          leaseCount: 1,
          leaseIds: row.id ? [row.id] : [],
          leaseFolios: [row.folio],
          pendingBalancesByMonth: row.pendingBalancesByMonth.map(m => ({ ...m }))
        });
        return;
      }

      existing.leaseCount = (existing.leaseCount || 1) + 1;
      existing.leaseIds = [...(existing.leaseIds || []), ...(row.id ? [row.id] : [])];
      existing.leaseFolios = [...(existing.leaseFolios || []), row.folio];
      existing.totalAccumulatedDebt = this.round2(existing.totalAccumulatedDebt + row.totalAccumulatedDebt);
      existing.totalExpected = this.round2(existing.totalExpected + row.totalExpected);
      existing.totalPaid = this.round2(existing.totalPaid + row.totalPaid);
      existing.totalOverdueDays = Math.max(existing.totalOverdueDays, row.totalOverdueDays);

      const byMonth = new Map<string, PendingBalanceByMonth>();
      existing.pendingBalancesByMonth.forEach(m => byMonth.set(m.month, m));
      row.pendingBalancesByMonth.forEach(m => {
        const prev = byMonth.get(m.month);
        if (prev) {
          prev.amount = this.round2(prev.amount + m.amount);
          prev.expectedAmount = this.round2(prev.expectedAmount + m.expectedAmount);
          prev.paidAmount = this.round2(prev.paidAmount + m.paidAmount);
        } else {
          byMonth.set(m.month, { ...m });
        }
      });
      existing.pendingBalancesByMonth = Array.from(byMonth.values());
    });

    return Array.from(byClient.values());
  }

  onGroupingChange(): void {
    this.rebuildDisplayData();
    this.applySortingAndFilters();
    this.cd.markForCheck();
  }

  calculateMetrics(data: PendingBalancesResponse): void {
    const clients = data.clientsData || [];

    // Métricas basadas en clientes únicos (no arrendamientos)
    const uniqueClientIds = new Set(clients.map(c => c.clientId || c.clientName));
    const clientDebt = new Map<string, number>();
    const clientMaxOverdue = new Map<string, number>();

    clients.forEach(c => {
      const key = c.clientId || c.clientName;
      clientDebt.set(key, (clientDebt.get(key) || 0) + c.totalAccumulatedDebt);
      clientMaxOverdue.set(key, Math.max(clientMaxOverdue.get(key) || 0, c.totalOverdueDays));
    });

    const totalDebt = Array.from(clientDebt.values()).reduce((s, v) => s + v, 0);
    const overdueValues = Array.from(clientMaxOverdue.values());

    this.metrics = {
      totalClients: uniqueClientIds.size,
      totalDebt,
      averageOverdueDays: overdueValues.length > 0
        ? Math.round(overdueValues.reduce((s, v) => s + v, 0) / overdueValues.length)
        : 0,
      criticalClients: overdueValues.filter(d => d > 60).length,
      monthWithHighestDebt: this.getMonthWithHighestDebt(data.monthlyTotals || {})
    };

    if (clients.length > 0) {
      this.maxDebt = Math.max(...Array.from(clientDebt.values()));
      this.debtRange = [0, this.maxDebt];
    } else {
      this.maxDebt = 1000000;
      this.debtRange = [0, this.maxDebt];
    }
  }

  getMonthWithHighestDebt(monthlyTotals: { [month: string]: number }): string {
    return Object.entries(monthlyTotals)
      .reduce((max, [month, amount]) => amount > max.amount ? { month, amount } : max, 
              { month: '', amount: 0 }).month;
  }

  generateCharts(data: PendingBalancesResponse): void {
    if (isPlatformBrowser(this.platformId)) {
      this.monthlyTotalsChart = this.generateMonthlyTotalsChart(data.monthlyTotals);
      this.overdueAnalysisChart = this.generateOverdueDebtByRangeChart(data.clientsData);
    }
  }

  // Redondea a centavos. El backend ya entrega 2 decimales; esto contiene el arrastre de coma
  // flotante que aparece al restar y al sumar filas (p. ej. 196945.19999999998).
  private round2(n: number): number {
    return Math.round((n || 0) * 100) / 100;
  }

  // Texto de moneda para atributos (tooltips), donde no se puede usar el pipe de la plantilla.
  fmt(n: number): string {
    return (n || 0).toLocaleString('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  private buildCell(balance: PendingBalanceByMonth | null | undefined): MonthlyCellData {
    if (!balance || balance.expectedAmount === 0) {
      return { expected: 0, paid: 0, pending: 0, status: 'no-period' };
    }
    const { expectedAmount: expected, paidAmount: paid } = balance;
    const pending = this.round2(Math.max(0, expected - paid));
    let status: MonthlyCellData['status'] = 'unpaid';
    if (paid >= expected) status = 'paid';
    else if (paid > 0) status = 'partial';
    return { expected, paid, pending, status };
  }

  getCellStatus(cell: MonthlyCellData | null | undefined): MonthlyCellData['status'] {
    return cell?.status || 'no-period';
  }

  getPaidPercent(cell: MonthlyCellData | null | undefined): number {
    if (!cell || cell.expected === 0) return 0;
    return Math.min(100, Math.max(0, (cell.paid / cell.expected) * 100));
  }

  generateMonthlyDebtMatrix(rows: PendingBalancesByClient[]): void {
    const allMonths = new Set<string>();
    rows.forEach(row => row.pendingBalancesByMonth.forEach(b => allMonths.add(b.month)));

    const sortedMonths = Array.from(allMonths).sort((a, b) => b.localeCompare(a));
    const last12Months = sortedMonths.slice(0, 12);

    this.matrixMonths = last12Months.map(month => {
      const [year, monthNum] = month.split('-');
      const date = new Date(parseInt(year), parseInt(monthNum) - 1);
      return {
        key: month,
        label: date.toLocaleDateString('es-MX', { month: 'short', year: '2-digit' }),
        year: parseInt(year),
        month: parseInt(monthNum)
      };
    });

    this.monthlyDebtMatrix = rows
      .filter(row => row.pendingBalancesByMonth.length > 0)
      .map(row => {
        const byMonth = new Map<string, PendingBalanceByMonth>();
        row.pendingBalancesByMonth.forEach(b => byMonth.set(b.month, b));

        const monthlyDebts: { [month: string]: MonthlyCellData } = {};
        last12Months.forEach(month => {
          monthlyDebts[month] = this.buildCell(byMonth.get(month));
        });

        return {
          clientId: row.clientId || row.id || row.folio,
          clientName: row.clientName,
          folio: row.folio,
          rowType: row.rowType || 'lease',
          leaseCount: row.leaseCount || 1,
          leaseIds: row.leaseIds || (row.id ? [row.id] : []),
          leaseFolios: row.leaseFolios || [row.folio],
          monthlyDebts,
          totalDebt: row.totalAccumulatedDebt,
          totalExpected: row.totalExpected,
          totalPaid: row.totalPaid
        } as MonthlyDebtMatrix;
      })
      .sort((a, b) => b.totalDebt - a.totalDebt);
  }

  generateMonthlyTotalsChart(monthlyTotals: { [month: string]: number }): any {
    const months = Object.keys(monthlyTotals).sort();
    const amounts = months.map(month => monthlyTotals[month]);
    
    return {
      labels: months.map(month => this.formatMonthLabel(month)),
      datasets: [{
        label: 'Saldo Vencido por Mes',
        data: amounts,
        backgroundColor: [
          '#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6'
        ],
        borderColor: '#dc2626',
        borderWidth: 1,
        borderRadius: 4,
        borderSkipped: false,
      }]
    };
  }

  generateOverdueDebtByRangeChart(clientsData: PendingBalancesByClient[]): any {
    const ranges = [
      { label: '1-30 días', min: 1, max: 30, color: '#22c55e' },
      { label: '31-60 días', min: 31, max: 60, color: '#eab308' },
      { label: '61-90 días', min: 61, max: 90, color: '#f97316' },
      { label: 'Más de 90 días', min: 91, max: 999, color: '#ef4444' }
    ];

    const rangeData = ranges.map(range => {
      const clientsInRange = clientsData.filter(client => 
        client.totalOverdueDays >= range.min && client.totalOverdueDays <= range.max
      );
      
      return {
        range: range.label,
        totalDebt: clientsInRange.reduce((sum, client) => sum + client.totalAccumulatedDebt, 0),
        clientCount: clientsInRange.length,
        color: range.color
      };
    });

    return {
      labels: rangeData.map(item => item.range),
      datasets: [{
        label: 'Deuda Total por Rango de Días',
        data: rangeData.map(item => item.totalDebt),
        backgroundColor: rangeData.map(item => item.color),
        borderColor: rangeData.map(item => item.color),
        borderWidth: 1,
        borderRadius: 4,
        borderSkipped: false,
        clientCounts: rangeData.map(item => item.clientCount) // Para tooltips
      }]
    };
  }

  initializeChartOptions(): void {
    this.barChartOptions = {
      plugins: {
        legend: { display: true, position: 'top' },
        title: { 
          display: false
        },
        tooltip: {
          callbacks: {
            label: (context: any) => {
              return `$${context.parsed.y.toLocaleString('es-MX', { 
                minimumFractionDigits: 2 
              })}`;
            }
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            callback: (value: any) => `$${value.toLocaleString('es-MX')}`
          }
        }
      },
      responsive: true,
      maintainAspectRatio: false
    };

    this.overdueRangeChartOptions = {
      plugins: {
        legend: { display: true, position: 'top' },
        title: { 
          display: false
        },
        tooltip: {
          callbacks: {
            label: (context: any) => {
              const dataset = context.dataset;
              const index = context.dataIndex;
              const clientCount = dataset.clientCounts ? dataset.clientCounts[index] : 0;
              const debtAmount = context.parsed.y;
              
              return [
                `Rango: ${context.label}`,
                `Clientes: ${clientCount}`,
                `Deuda Total: $${debtAmount.toLocaleString('es-MX', { minimumFractionDigits: 0 })}`
              ];
            }
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            callback: (value: any) => `$${value.toLocaleString('es-MX')}`
          }
        }
      },
      responsive: true,
      maintainAspectRatio: false
    };

    this.scatterChartOptions = {
      plugins: {
        legend: { display: true },
        title: { 
          display: false
        },
        tooltip: {
          callbacks: {
            label: (context: any) => {
              const point = context.raw;
              return [
                `Cliente: ${point.clientName}`,
                `Folio: ${point.folio}`,
                `Días: ${point.x}`,
                `Deuda: $${point.y.toLocaleString('es-MX')}`
              ];
            }
          }
        }
      },
      scales: {
        x: { 
          title: { display: true, text: 'Días de Atraso' },
          beginAtZero: true
        },
        y: { 
          title: { display: true, text: 'Deuda Total ($)' },
          beginAtZero: true,
          ticks: {
            callback: (value: any) => `$${value.toLocaleString('es-MX')}`
          }
        }
      },
      responsive: true,
      maintainAspectRatio: false
    };

    this.lineChartOptions = {
      plugins: {
        legend: { display: true },
        title: { 
          display: true, 
          text: 'Evolución de Deuda Mensual por Cliente',
          font: { size: 16, weight: 'bold' }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            callback: (value: any) => `$${value.toLocaleString('es-MX')}`
          }
        }
      },
      responsive: true,
      maintainAspectRatio: false
    };
  }

  // Utility methods
  formatMonthLabel(month: string): string {
    const [year, monthNum] = month.split('-');
    const date = new Date(parseInt(year), parseInt(monthNum) - 1);
    return date.toLocaleDateString('es-MX', { 
      month: 'short', 
      year: 'numeric' 
    });
  }

  truncateClientName(name: string, maxLength: number): string {
    return name.length > maxLength 
      ? name.substring(0, maxLength) + '...' 
      : name;
  }

  getDebtSeverityClass(debt: number): string {
    if (debt > 100000) return 'text-red-500 font-bold';
    if (debt > 50000) return 'text-orange-500 font-semibold';
    if (debt > 25000) return 'text-yellow-600';
    return 'text-green-600';
  }

  getOverdueSeverity(days: number): 'success' | 'warning' | 'danger' | 'info' {
    if (days > 90) return 'danger';
    if (days > 60) return 'warning';
    if (days > 30) return 'info';
    return 'success';
  }

  // Monthly Matrix Helper Methods
  getDebtBarWidth(amount: number, maxAmount: number): number {
    if (maxAmount === 0) return 0;
    return Math.max(5, (amount / maxAmount) * 100);
  }

  getDebtBarColor(amount: number): string {
    if (amount === 0) return '#e5e7eb';
    if (amount > 100000) return '#ef4444';
    if (amount > 50000) return '#f97316';
    if (amount > 25000) return '#eab308';
    return '#22c55e';
  }

  getMaxDebtForMatrix(): number {
    if (this.monthlyDebtMatrix.length === 0) return 0;
    let maxDebt = 0;
    this.monthlyDebtMatrix.forEach(client => {
      Object.values(client.monthlyDebts).forEach(cell => {
        if (cell.pending > maxDebt) maxDebt = cell.pending;
      });
    });
    return maxDebt;
  }

  // Helper para la tabla inferior: % pagado a nivel de row
  getRowPaidPercent(row: PendingBalancesByClient): number {
    if (!row || !row.totalExpected) return 0;
    return Math.min(100, Math.max(0, (row.totalPaid / row.totalExpected) * 100));
  }

  toggleMonthlyMatrix(): void {
    this.showMonthlyMatrix = !this.showMonthlyMatrix;
  }

  trackByClientId(index: number, client: MonthlyDebtMatrix): string {
    return client.clientId;
  }

  // Navigation methods
  navigateToLease(client: PendingBalancesByClient): void {
    if (client.id) {
      this.router.navigate(['/lease', client.id]);
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: 'Advertencia',
        detail: 'No se encontró el ID del arrendamiento',
        life: 3000
      });
    }
  }

  navigateToLeaseFromMatrix(matrixClient: MonthlyDebtMatrix): void {
    // En vista por cliente puede haber N arrendamientos; navegamos al primero.
    const leaseId = matrixClient.leaseIds?.[0] || matrixClient.clientId;
    if (leaseId) {
      this.router.navigate(['/lease', leaseId]);
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: 'Advertencia',
        detail: 'No se encontró el ID del arrendamiento',
        life: 3000
      });
    }
  }

  // Event handlers
  showMonthlyDetail(client: PendingBalancesByClient): void {
    this.selectedClient = client;
    this.displayMonthlyModal = true;
    
    if (isPlatformBrowser(this.platformId)) {
      this.clientMonthlyChart = {
        labels: client.pendingBalancesByMonth.map(item => this.formatMonthLabel(item.month)),
        datasets: [{
          label: 'Adeudo por Mes',
          data: client.pendingBalancesByMonth.map(item => item.amount),
          borderColor: '#ef4444',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          tension: 0.4,
          fill: true
        }]
      };
    }
  }

  contactClient(client: PendingBalancesByClient): void {
    this.messageService.add({
      severity: 'info',
      summary: 'Funcionalidad en Desarrollo',
      detail: 'La función de envío de emails se implementará próximamente',
      life: 4000
    });
  }

  generateStatement(client: PendingBalancesByClient): void {
    this.messageService.add({
      severity: 'info',
      summary: 'Funcionalidad en Desarrollo',
      detail: 'La generación de estados de cuenta se implementará próximamente',
      life: 4000
    });
  }

  // Filter methods
  applyFilters(): void {
    const source = this.displayClientsData.length > 0 || this.groupBy === 'client'
      ? this.displayClientsData
      : this.clientsData;

    const search = this.globalFilter.toLowerCase();

    this.filteredClientsData = source.filter(client => {
      const overdueFilter = !this.selectedOverdueRange ||
                           this.selectedOverdueRange.value === 'all' ||
                           (client.totalOverdueDays >= this.selectedOverdueRange.min &&
                            client.totalOverdueDays <= this.selectedOverdueRange.max);

      if (!search) return overdueFilter;

      const nameMatch = client.clientName.toLowerCase().includes(search);
      const folioMatch = this.groupBy === 'lease'
        ? client.folio.toLowerCase().includes(search)
        : (client.leaseFolios || []).some(f => f.toLowerCase().includes(search));

      return overdueFilter && (nameMatch || folioMatch);
    });
  }

  // Sorting methods
  onSort(field: string): void {
    // Si es la misma columna, alternar orden
    if (this.sortField === field) {
      if (this.sortOrder === 'asc') {
        this.sortOrder = 'desc';
      } else if (this.sortOrder === 'desc') {
        // Quitar sorting
        this.sortField = null;
        this.sortOrder = null;
      } else {
        this.sortOrder = 'asc';
      }
    } else {
      // Nueva columna seleccionada
      this.sortField = field;
      this.sortOrder = 'asc';
    }
    
    this.applySortingAndFilters();
  }

  applySortingAndFilters(): void {
    // Primero aplicar filtros existentes
    this.applyFilters();
    
    // Luego aplicar sorting si está activo
    if (this.sortField && this.sortOrder) {
      this.filteredClientsData = this.filteredClientsData.sort((a, b) => {
        let valueA, valueB;
        
        switch (this.sortField) {
          case 'totalAccumulatedDebt':
            valueA = a.totalAccumulatedDebt;
            valueB = b.totalAccumulatedDebt;
            break;
          case 'totalOverdueDays':
            valueA = a.totalOverdueDays;
            valueB = b.totalOverdueDays;
            break;
          case 'periods':
            valueA = a.pendingBalancesByMonth.length;
            valueB = b.pendingBalancesByMonth.length;
            break;
          default:
            return 0;
        }
        
        if (this.sortOrder === 'asc') {
          return valueA - valueB;
        } else {
          return valueB - valueA;
        }
      });
    }
  }

  getSortIconClass(field: string): string {
    if (this.sortField !== field) {
      return 'pi pi-sort text-400'; // Icono neutral
    }
    
    if (this.sortOrder === 'asc') {
      return 'pi pi-sort-up text-primary';
    } else if (this.sortOrder === 'desc') {
      return 'pi pi-sort-down text-primary';
    }
    
    return 'pi pi-sort text-400';
  }

  onOverdueRangeChange(): void {
    this.applySortingAndFilters();
  }

  onGlobalFilterChange(): void {
    this.applySortingAndFilters();
  }

  exportToExcel(): void {
    // Validar que hay datos para exportar
    if (!this.filteredClientsData || this.filteredClientsData.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atención',
        detail: 'No hay datos para exportar. Aplique filtros o verifique que existan registros.',
        life: 5000
      });
      return;
    }

    // Mostrar spinner de carga
    this.loaderService.showSpinner();

    try {
      const folioHeader = this.groupBy === 'client' ? 'Arrendamientos' : 'Folio';
      // Número, no texto. Una celda "$1,234.56" en Excel no se suma ni se filtra, y la
      // conciliación de centavos se hace ahí. Sale el valor crudo a 2 decimales y que Excel
      // le ponga formato de moneda.
      const currency = (n: number) => this.round2(n);

      const excelData = this.filteredClientsData.map(client => {
        const oldestMonth = client.pendingBalancesByMonth.length > 0
          ? this.formatMonthLabel(client.pendingBalancesByMonth[0].month)
          : 'N/A';

        const folioCell = this.groupBy === 'client'
          ? (client.leaseFolios || [client.folio]).join(', ')
          : client.folio;

        return {
          [folioHeader]: folioCell,
          'Cliente': client.clientName,
          'Esperado': currency(client.totalExpected),
          'Pagado': currency(client.totalPaid),
          'Deuda Total': currency(client.totalAccumulatedDebt),
          '% Pagado': `${this.getRowPaidPercent(client).toFixed(1)}%`,
          'Días de Atraso': client.totalOverdueDays,
          'Períodos Vencidos': client.pendingBalancesByMonth.length,
          'Mes Más Antiguo': oldestMonth,
          'Estado': client.totalOverdueDays > 90 ? 'Crítico' :
                   client.totalOverdueDays > 60 ? 'Alto Riesgo' :
                   client.totalOverdueDays > 30 ? 'Moderado' : 'Bajo Riesgo',
          'Fecha de Exportación': new Date().toLocaleDateString('es-MX')
        };
      });

      const totalDebt = this.filteredClientsData.reduce((sum, c) => sum + c.totalAccumulatedDebt, 0);
      const totalExpected = this.filteredClientsData.reduce((sum, c) => sum + c.totalExpected, 0);
      const totalPaid = this.filteredClientsData.reduce((sum, c) => sum + c.totalPaid, 0);
      const averageDays = this.filteredClientsData.length > 0
        ? Math.round(this.filteredClientsData.reduce((sum, c) => sum + c.totalOverdueDays, 0) / this.filteredClientsData.length)
        : 0;
      const criticalCount = this.filteredClientsData.filter(c => c.totalOverdueDays > 60).length;
      const globalPercent = totalExpected > 0 ? (totalPaid / totalExpected) * 100 : 0;

      excelData.push({
        [folioHeader]: '--- RESUMEN ---',
        'Cliente': `Total: ${this.filteredClientsData.length}`,
        'Esperado': currency(totalExpected),
        'Pagado': currency(totalPaid),
        'Deuda Total': currency(totalDebt),
        '% Pagado': `${globalPercent.toFixed(1)}%`,
        'Días de Atraso': averageDays,
        'Períodos Vencidos': criticalCount,
        'Mes Más Antiguo': 'PROMEDIO',
        'Estado': 'RESUMEN',
        'Fecha de Exportación': new Date().toLocaleDateString('es-MX')
      });

      const suffix = this.groupBy === 'client' ? '_por_cliente' : '_por_arrendamiento';
      const fileName = `Saldos_Pendientes${suffix}_${new Date().toISOString().split('T')[0]}`;
      this.dataTableService.exportExcel(excelData, fileName);

      // Ocultar spinner y mostrar mensaje de éxito
      this.loaderService.hideSpinner();
      this.messageService.add({
        severity: 'success',
        summary: 'Éxito',
        detail: `Excel generado exitosamente con ${this.filteredClientsData.length} registros.`,
        life: 5000
      });

    } catch (error) {
      // Manejar errores
      console.error('Error al generar Excel:', error);
      this.loaderService.hideSpinner();
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Error al generar el archivo Excel. Inténtelo nuevamente.',
        life: 5000
      });
    }
  }

  exportMatrixToExcel(): void {
    // Validar que hay datos para exportar
    if (!this.monthlyDebtMatrix || this.monthlyDebtMatrix.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atención',
        detail: 'No hay datos de matriz mensual para exportar.',
        life: 5000
      });
      return;
    }

    // Mostrar spinner de carga
    this.loaderService.showSpinner();

    try {
      const allMonths = new Set<string>();
      this.monthlyDebtMatrix.forEach(client => {
        Object.keys(client.monthlyDebts).forEach(month => allMonths.add(month));
      });

      const sortedMonths = Array.from(allMonths).sort((a, b) => b.localeCompare(a));

      // Número, no texto — ver la nota en exportToExcel.
      const currency = (n: number) => this.round2(n);
      const folioHeader = this.groupBy === 'client' ? 'Arrendamientos' : 'Folio';

      const excelData = this.monthlyDebtMatrix.map(client => {
        const folioCell = this.groupBy === 'client'
          ? (client.leaseFolios && client.leaseFolios.length > 0
              ? client.leaseFolios.join(', ')
              : `${client.leaseCount} arrend.`)
          : client.folio;

        const row: any = {
          [folioHeader]: folioCell,
          'Cliente': client.clientName,
        };

        sortedMonths.forEach(month => {
          const monthLabel = this.formatMonthLabel(month);
          const cell = client.monthlyDebts[month];
          if (!cell || cell.status === 'no-period') {
            row[`${monthLabel} Esperado`] = '-';
            row[`${monthLabel} Pagado`] = '-';
            row[`${monthLabel} Pendiente`] = '-';
          } else {
            row[`${monthLabel} Esperado`] = currency(cell.expected);
            row[`${monthLabel} Pagado`] = currency(cell.paid);
            row[`${monthLabel} Pendiente`] = cell.pending > 0 ? currency(cell.pending) : '-';
          }
        });

        row['Esperado Total'] = currency(client.totalExpected);
        row['Pagado Total'] = currency(client.totalPaid);
        row['Deuda Total'] = currency(client.totalDebt);
        row['% Pagado'] = client.totalExpected > 0
          ? `${((client.totalPaid / client.totalExpected) * 100).toFixed(1)}%`
          : '0.0%';

        return row;
      });

      const totalsRow: any = {
        [folioHeader]: '--- TOTALES ---',
        'Cliente': 'RESUMEN',
      };

      let grandExpected = 0;
      let grandPaid = 0;
      let grandPending = 0;

      sortedMonths.forEach(month => {
        const monthLabel = this.formatMonthLabel(month);
        const monthExpected = this.monthlyDebtMatrix.reduce((s, c) => s + (c.monthlyDebts[month]?.expected || 0), 0);
        const monthPaid = this.monthlyDebtMatrix.reduce((s, c) => s + (c.monthlyDebts[month]?.paid || 0), 0);
        const monthPending = this.monthlyDebtMatrix.reduce((s, c) => s + (c.monthlyDebts[month]?.pending || 0), 0);
        grandExpected += monthExpected;
        grandPaid += monthPaid;
        grandPending += monthPending;
        totalsRow[`${monthLabel} Esperado`] = monthExpected > 0 ? currency(monthExpected) : '-';
        totalsRow[`${monthLabel} Pagado`] = monthPaid > 0 ? currency(monthPaid) : '-';
        totalsRow[`${monthLabel} Pendiente`] = monthPending > 0 ? currency(monthPending) : '-';
      });

      const totalExpectedAll = this.monthlyDebtMatrix.reduce((s, c) => s + c.totalExpected, 0);
      const totalPaidAll = this.monthlyDebtMatrix.reduce((s, c) => s + c.totalPaid, 0);
      const totalDebtAll = this.monthlyDebtMatrix.reduce((s, c) => s + c.totalDebt, 0);

      totalsRow['Esperado Total'] = currency(totalExpectedAll);
      totalsRow['Pagado Total'] = currency(totalPaidAll);
      totalsRow['Deuda Total'] = currency(totalDebtAll);
      totalsRow['% Pagado'] = totalExpectedAll > 0
        ? `${((totalPaidAll / totalExpectedAll) * 100).toFixed(1)}%`
        : '0.0%';

      excelData.push(totalsRow);

      const suffix = this.groupBy === 'client' ? '_por_cliente' : '_por_arrendamiento';
      const fileName = `Matriz_Deuda_Mensual${suffix}_${new Date().toISOString().split('T')[0]}`;
      this.dataTableService.exportExcel(excelData, fileName);

      // Ocultar spinner y mostrar mensaje de éxito
      this.loaderService.hideSpinner();
      this.messageService.add({
        severity: 'success',
        summary: 'Éxito',
        detail: `Matriz de deuda mensual exportada exitosamente con ${this.monthlyDebtMatrix.length} clientes.`,
        life: 5000
      });

    } catch (error) {
      // Manejar errores
      console.error('Error al generar Excel de matriz:', error);
      this.loaderService.hideSpinner();
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Error al generar el archivo Excel de la matriz. Inténtelo nuevamente.',
        life: 5000
      });
    }
  }

  exportToPDF(): void {
    console.log('Exportar a PDF');
    // Implementar exportación a PDF
  }

  refresh(): void {
    this.loadPendingBalances();
  }
}